/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import LoadingDots from '@design-system/loading-dots';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import { IBlock, ETheme, TTone } from '@datatype/type';

export type ChipSize = 'tiny' | 'small' | 'medium' | 'large' | 'big';

interface IProps {
  isSelected?: boolean;
  isLoading?: boolean;
  size?: ChipSize;
  handleClick?: () => void;
  text: string;
  width?: string;
  shape?: 'square' | 'round';
  usage?: 'static' | 'button'; // default: 'button'
  kind?: 'filled' | 'outlined'; // default: undefined (uses isSelected logic)
  theme?: ETheme;
  tone?: TTone; // default: 'strong'
}

const createDesign = (
  chipSize: ChipSize,
  isSelected: boolean,
  width?: string,
  shape?: 'square' | 'round',
  usage: 'static' | 'button' = 'button',
  kind?: 'filled' | 'outlined',
  theme?: ETheme,
  tone: TTone = 'strong',
): Pick<IBlock['design'], 'box' | 'text'> => {
  let textSize: 'tiny' | 'small' | 'medium' | 'large' | 'big';
  let padding: string;

  switch (chipSize) {
    case 'tiny':
      textSize = 'tiny';
      padding = '1px 3px';
      break;
    case 'small':
      textSize = 'small';
      padding = '2px 6px';
      break;
    case 'large':
      textSize = 'large';
      padding = '10px 20px';
      break;
    case 'big':
      textSize = 'big';
      padding = '12px 24px';
      break;
    case 'medium':
    default:
      textSize = 'medium';
      padding = '8px 16px';
      break;
  }

  // Determine theme color (tone: strong=_1, normal=_2, light=_3)
  // BLACK 계열은 _1=#000 가독성 문제로 strong=_2(=BLACK_2)부터 시작
  const toneIndex = tone === 'normal' ? 2 : tone === 'light' ? 3 : 1;
  const themePalettes: Partial<Record<ETheme, string[]>> = {
    // Chip은 custom 색상 props를 지원하지 않으므로 기존 기본값인 BROWN 팔레트를 사용한다.
    [ETheme.CUSTOM]: [color.BROWN_1, color.BROWN_2, color.BROWN_3],
    [ETheme.BLACK]: [color.BLACK_2, color.BLACK_3, color.BLACK_5],
    [ETheme.GRAY]: [color.GRAY_1, color.GRAY_2, color.GRAY_3],
    [ETheme.BLUE]: [color.BLUE_2, color.BLUE_3, color.BLUE_4],
    [ETheme.CYAN]: [color.CYAN_1, color.CYAN_2, color.CYAN_4],
    [ETheme.GREEN]: [color.GREEN_3, color.GREEN_4, color.GREEN_5],
    [ETheme.RED]: [color.RED_2, color.RED_3, color.RED_4],
    [ETheme.YELLOW]: [color.YELLOW_2, color.YELLOW_3, color.YELLOW_4],
    [ETheme.BROWN]: [color.BROWN_1, color.BROWN_2, color.BROWN_3],
    [ETheme.CREAM]: [color.BROWN_2, color.BROWN_3, color.BROWN_5],
    [ETheme.WHITE]: [color.WHITE_1, color.WHITE_1, color.WHITE_1],
  };
  // NONE 등 Chip에서 지원하지 않는 테마는 기본 BROWN 팔레트를 사용한다.
  const palette = themePalettes[theme ?? ETheme.BROWN] ?? [color.BROWN_1, color.BROWN_2, color.BROWN_3];
  const themeColor = palette[toneIndex - 1] ?? palette[0];

  // Determine colors based on kind or isSelected
  let backgroundColor;
  let textColor;
  let borderColor;
  const isBold = isSelected;

  if (kind === 'filled') {
    backgroundColor = themeColor;
    textColor = color.WHITE_1;
    borderColor = themeColor;
  } else if (kind === 'outlined') {
    backgroundColor = 'transparent';
    textColor = themeColor;
    borderColor = themeColor;
  } else {
    // Default logic based on isSelected
    if (isSelected) {
      backgroundColor = themeColor;
      textColor = color.WHITE_1;
      borderColor = themeColor;
    } else {
      // Not selected (default state)
      if (usage === 'static') {
        backgroundColor = color.WHITE_1;
        textColor = themeColor;
        borderColor = themeColor;
      }
      // else (button usage) -> default unselected state (gray border handled below by initial borderColor value)
    }
  }

  /* logic to determine hover style */
  const isButton = usage === 'button';
  const isFilled = backgroundColor !== color.WHITE_1 && backgroundColor !== 'transparent';
  let hoverStyle = '';

  if (isButton) {
    if (isFilled) {
      hoverStyle = `filter: brightness(0.9);`;
    } else {
      // Outlined: increase border thickness (simulated with box-shadow to avoid layout shift)
      hoverStyle = `box-shadow: inset 0 0 0 1px ${borderColor}; border-color: ${borderColor};`;
    }
  }

  return {
    text: {
      size: textSize,
      color: textColor,
      bold: isBold,
    },
    box: {
      width,
      padding: padding,
      borderRadius: shape === 'square' ? '0px' : size.BORDER_RADIUS_1,
      borderWidth: '1px',
      borderColor: borderColor,
      backgroundColor: backgroundColor,
      event: isButton
        ? {
            cursor: 'pointer',
            hover: hoverStyle,
          }
        : undefined,
    },
  };
};

const Wrapper = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  position: relative;
  width: ${(props) => props.$design.box?.width || 'fit-content'};
`;
const LoadingContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Chip: React.FC<IProps> = ({
  isSelected = false,
  isLoading = false,
  size = 'medium',
  handleClick,
  text,
  width,
  shape,
  usage = 'button',
  kind,
  theme = ETheme.GRAY,
  tone = 'strong',
}) => {
  const design = createDesign(size, isSelected, width, shape, usage, kind, theme, tone);
  if (!isLoading) return <TextBlock text={text} design={design} click={{ handler: handleClick }} />;

  return (
    <Wrapper $design={design}>
      <TextBlock text={text} design={{ ...design, text: { ...design.text, color: 'transparent' } }} />
      <LoadingContainer>
        <LoadingDots color={design.text?.color} />
      </LoadingContainer>
    </Wrapper>
  );
};

export default Chip;
